import { UNIT_BOX, UNIT_ML, UNIT_PIECE } from "./common";
import type { PackDef } from "./types";

const DOSAGE_FORMS = [
  { value: "tablet", label: { en: "Tablet", bn: "ট্যাবলেট" } },
  { value: "capsule", label: { en: "Capsule", bn: "ক্যাপসুল" } },
  { value: "syrup", label: { en: "Syrup", bn: "সিরাপ" } },
  { value: "suspension", label: { en: "Suspension", bn: "সাসপেনশন" } },
  { value: "injection", label: { en: "Injection", bn: "ইনজেকশন" } },
  { value: "cream", label: { en: "Cream / ointment", bn: "ক্রিম / মলম" } },
  { value: "drops", label: { en: "Drops", bn: "ড্রপ" } },
  { value: "inhaler", label: { en: "Inhaler", bn: "ইনহেলার" } },
  { value: "powder", label: { en: "Powder", bn: "পাউডার" } },
  { value: "other", label: { en: "Other", bn: "অন্যান্য" } },
] as const;

export const PHARMACY_PACK: PackDef = {
  id: "pharmacy",
  version: 1,
  available: true,
  name: { en: "Pharmacy", bn: "ফার্মেসি" },
  description: {
    en: "Medicine shops and pharmacies with batch and expiry tracking",
    bn: "ব্যাচ ও মেয়াদ ট্র্যাকিং সহ ওষুধের দোকান ও ফার্মেসি",
  },
  icon: "pill",
  modules: {
    variants: false,
    batches: true,
    expiry: true,
    serials: false,
    vat: false,
    multi_branch: false,
    storefront: false,
    loans: false,
  },
  baseUnits: [
    UNIT_PIECE,
    { code: "strip", name: { en: "Strip", bn: "পাতা" }, fractional: false },
    UNIT_BOX,
    { code: "bottle", name: { en: "Bottle", bn: "বোতল" }, fractional: false },
    { code: "tube", name: { en: "Tube", bn: "টিউব" }, fractional: false },
    { code: "vial", name: { en: "Vial", bn: "ভায়াল" }, fractional: false },
    UNIT_ML,
  ],
  // Pack sizes (box = 10 strips = 100 pieces) differ per medicine, so they are set on each item.
  unitConversions: [],
  categories: [
    { en: "Tablets", bn: "ট্যাবলেট" },
    { en: "Capsules", bn: "ক্যাপসুল" },
    { en: "Syrups & suspensions", bn: "সিরাপ ও সাসপেনশন" },
    { en: "Injections", bn: "ইনজেকশন" },
    { en: "Creams & ointments", bn: "ক্রিম ও মলম" },
    { en: "Drops", bn: "ড্রপ" },
    { en: "Inhalers", bn: "ইনহেলার" },
    { en: "Supplements", bn: "সাপ্লিমেন্ট" },
    { en: "Medical devices", bn: "মেডিকেল ডিভাইস" },
    { en: "Baby care", bn: "শিশু যত্ন" },
    { en: "Personal care", bn: "ব্যক্তিগত যত্ন" },
  ],
  customFields: [
    {
      key: "generic_name",
      entity: "item",
      type: "text",
      label: { en: "Generic name", bn: "জেনেরিক নাম" },
      required: false,
      searchable: true,
    },
    {
      key: "strength",
      entity: "item",
      type: "text",
      label: { en: "Strength", bn: "শক্তি" },
      required: false,
      searchable: true,
    },
    {
      key: "dosage_form",
      entity: "item",
      type: "select",
      label: { en: "Dosage form", bn: "ডোজের ধরন" },
      required: false,
      searchable: false,
      options: DOSAGE_FORMS,
    },
    {
      key: "manufacturer",
      entity: "item",
      type: "text",
      label: { en: "Manufacturer", bn: "প্রস্তুতকারক" },
      required: false,
      searchable: true,
    },
    {
      key: "dar_number",
      entity: "item",
      type: "text",
      label: { en: "DAR / registration no.", bn: "ডিএআর / নিবন্ধন নং" },
      required: false,
      searchable: true,
    },
    {
      key: "rack",
      entity: "item",
      type: "text",
      label: { en: "Rack / shelf", bn: "র‍্যাক / তাক" },
      required: false,
      searchable: true,
    },
  ],
  receiptTemplate: "pharmacy",
  dashboard: [
    "sales_today",
    "gross_profit",
    "cash_in_hand",
    "dues_receivable",
    "near_expiry",
    "low_stock",
    "sales_trend",
  ],
  nearExpiryDays: 90,
};
